import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTicketFilters } from '@/hooks/useTicketFilters';
import { cn } from '@/lib/utils';

interface TicketPaginationProps {
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  isFetching: boolean;
}

export const TicketPagination = ({
  total,
  totalPages,
  hasNext,
  hasPrev,
  isFetching,
}: TicketPaginationProps) => {
  const { filters, setPage } = useTicketFilters();
  const currentPage = filters.page;

  if (totalPages <= 1)
    return (
      <div className="px-4 py-2.5 border-t border-border">
        <span className="text-xs text-muted-foreground tabular-nums">
          {total} ticket{total !== 1 ? 's' : ''}
        </span>
      </div>
    );

  const getPageNumbers = (): (number | '...')[] => {
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    const range: (number | '...')[] = [];

    for (let i = left; i <= right; i++) range.push(i);
    if (left > 2) range.unshift('...');
    if (left > 1) range.unshift(1);
    if (right < totalPages - 1) range.push('...');
    if (right < totalPages) range.push(totalPages);

    return range;
  };

  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-background shrink-0">
      <span className="text-xs text-muted-foreground tabular-nums">
        {total} ticket{total !== 1 ? 's' : ''}
      </span>

      <div className="flex items-center gap-1">
        <PageButton
          onClick={() => setPage(currentPage - 1)}
          disabled={!hasPrev || isFetching}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </PageButton>

        {getPageNumbers().map((p, i) =>
          p === '...' ? (
            <span
              key={`ellipsis-${i}`}
              className="w-7 text-center text-xs text-muted-foreground"
            >
              ···
            </span>
          ) : (
            <PageButton
              key={p}
              onClick={() => setPage(Number(p))}
              disabled={isFetching}
              active={p === currentPage}
            >
              {p}
            </PageButton>
          ),
        )}

        <PageButton
          onClick={() => setPage(currentPage + 1)}
          disabled={!hasNext || isFetching}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </PageButton>
      </div>

      {/* Subtle fetching indicator */}
      <div className="w-16 flex justify-end">
        {isFetching && (
          <div className="w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        )}
      </div>
    </div>
  );
};

const PageButton = ({
  onClick,
  disabled,
  active,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  active?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'h-7 w-7 flex items-center justify-center rounded-md text-xs font-medium',
      'border transition-colors cursor-pointer',
      'disabled:opacity-40 disabled:cursor-not-allowed',
      active
        ? 'border-foreground/20 bg-foreground text-background'
        : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground',
    )}
  >
    {children}
  </button>
);
